import type { DropdownOption } from '../../app/shared/services/dropdown.service';
import type { SidebarNavItem } from '../sidebar-component/sidebar-component.component';

/**
 * Dashboard role model — the single source of truth for how the
 * `get-user-license-roles` payload maps onto the Dashboard's role views.
 *
 * The role selector used to be an in-page tab band (`app-role-tabs`); it now
 * lives in the sidebar as the Dashboard item's sub-menu (like Documents), with
 * one route per role (`/dashboard/management|performer|reviewer`). This module
 * carries the mapping logic that band and `DashboardPageComponent` shared, so
 * the sidebar (AppComponent), the routes, and the page shell all derive the
 * same answer from the same payload.
 */

/** The three Dashboard role views. Selects which sibling dashboard view renders. */
export type DashboardRole = 'management' | 'performer' | 'reviewer';

/**
 * Which variant the shared Management view represents. All four render the same
 * Management view; they differ only in the menu label and the role key the
 * dashboard APIs are called with.
 */
export type MgmtDashboardRole = 'MGMT' | 'CADMN' | 'DEPT' | 'AUDT';

/** `get-user-license-roles` row id for the Management dashboard role. */
const ROLE_ID_MANAGEMENT = 'MGMT';

/**
 * `get-user-license-roles` row id for the Company Admin role. It shares the
 * Management view — the menu item is labelled "Company Admin" and the dashboard
 * APIs are called with role `'CADMN'` instead of `'MGMT'`.
 */
const ROLE_ID_COMPANY_ADMIN = 'CADMN';

/** `get-user-license-roles` row id for the Department Role role. */
const ROLE_ID_DEPARTMENT_HEAD = 'DEPT';

/**
 * `get-user-license-roles` row id for the Auditor role. It shares the Management
 * view — the menu item is labelled "Auditor" and the dashboard APIs are called
 * with role `'AUDT'`, exactly as CADMN and DEPT already are.
 */
const ROLE_ID_AUDITOR = 'AUDT';

/** `get-user-license-roles` row id for the Performer role → Performer view. */
const ROLE_ID_PERFORMER = 'P';

/** `get-user-license-roles` row id for the Reviewer role → Reviewer view. */
const ROLE_ID_REVIEWER = 'R';

/**
 * The Management-view variants, in PRECEDENCE order — most specific first. The first
 * entry whose role id is present in the `get-user-license-roles` payload wins; plain
 * `MGMT` is the fallback when a management row names no variant.
 *
 * Ordered data rather than a nested ternary chain: the precedence is readable
 * top-to-bottom, and a future variant is one entry here instead of another branch
 * spliced in at the right depth. It is also the single list
 * {@link resolveDashboardRoleTabs} tests for "is this a management user at all?",
 * so the two cannot drift apart.
 */
const MANAGEMENT_ROLE_PRECEDENCE: readonly (readonly [roleId: string, dashboardRole: MgmtDashboardRole])[] = [
  [ROLE_ID_COMPANY_ADMIN, 'CADMN'],
  [ROLE_ID_DEPARTMENT_HEAD, 'DEPT'],
  [ROLE_ID_AUDITOR, 'AUDT'],
];

/**
 * Which Dashboard role views the `get-user-license-roles` payload authorizes, plus
 * the Management-view variant it resolved to. Produced by
 * {@link resolveDashboardRoleTabs}; consumed by `DashboardPageComponent` (view
 * gating) and {@link dashboardSidebarChildren} (sidebar sub-menu).
 */
export interface DashboardRoleTabs {
  /**
   * Whether the Management view is available. True ONLY when the payload contains
   * a Management (`MGMT`) row or a variant (CADMN/DEPT/AUDT). There is no longer a
   * "no P/R row" fallback: an empty payload used to fail OPEN into this view,
   * silently calling the dashboard APIs under a `MGMT` role the user was never
   * granted. All three flags false ⇒ the shell renders NO view and raises the
   * "No License Roles Assigned" popup instead.
   */
  showManagement: boolean;
  /** Whether the Performer view is available (a `P` row confirmed it). */
  showPerformer: boolean;
  /** Whether the Reviewer view is available (an `R` row confirmed it). */
  showReviewer: boolean;
  /**
   * Which Management-view variant this user resolved to (menu label + dashboard
   * API role). `'MGMT'` when Management is not shown (then unused).
   */
  mgmtRole: MgmtDashboardRole;
}

/**
 * Maps the `get-user-license-roles` rows onto the three view flags and the
 * Management variant: an `MGMT` (or variant) row enables the Management view, a
 * `P` row the Performer view, an `R` row the Reviewer view. An empty or
 * unrecognised payload (e.g. a 204 from the endpoint) enables NOTHING —
 * fail-closed for every view. The old behaviour ("keep Management so the
 * dashboard always renders a section") was fail-OPEN: it exposed the Management
 * view — and fired its `MGMT`-scoped dashboard APIs — for a user the server had
 * just confirmed holds no dashboard role at all. The shell now answers the
 * all-false case with the "No License Roles Assigned" popup and no view.
 * @param roles Role rows from the endpoint (`{ id, name }`), casing-tolerant.
 * @returns The resolved view flags + Management variant.
 */
export function resolveDashboardRoleTabs(roles: DropdownOption[]): DashboardRoleTabs {
  const ids = new Set(roles.map(role => String(role.id).trim().toUpperCase()));
  // Most specific variant wins (see MANAGEMENT_ROLE_PRECEDENCE); plain Management
  // is the fallback for a bare MGMT row.
  const variant = MANAGEMENT_ROLE_PRECEDENCE.find(([roleId]) => ids.has(roleId));

  return {
    // MGMT → Management, P → Performer, R → Reviewer. A view only ever unlocks on
    // a row the payload actually contains.
    showManagement: ids.has(ROLE_ID_MANAGEMENT) || variant !== undefined,
    showPerformer: ids.has(ROLE_ID_PERFORMER),
    showReviewer: ids.has(ROLE_ID_REVIEWER),
    mgmtRole: variant?.[1] ?? 'MGMT',
  };
}

/**
 * The role view the Dashboard opens on when no role route / persisted tab decides:
 * Management when present, else Performer, else Reviewer — the same precedence the
 * old in-page tab band used. `null` when the payload authorized NO view at all
 * (the confirmed-empty case) — the shell then renders no view and raises the
 * "No License Roles Assigned" popup instead of defaulting into one.
 * @param tabs Resolved view flags (see {@link resolveDashboardRoleTabs}).
 * @returns The default role view, or `null` when no view is available.
 */
export function defaultDashboardRole(tabs: DashboardRoleTabs): DashboardRole | null {
  return tabs.showManagement
    ? 'management'
    : tabs.showPerformer
      ? 'performer'
      : tabs.showReviewer
        ? 'reviewer'
        : null;
}

/**
 * Translate key for the Management view's menu label, per resolved variant.
 * (Formerly `RoleTabsComponent.mgmtLabelKey`.)
 * @param mgmtRole The resolved Management-view variant.
 * @returns An i18n key under `dashboard.*` (present in BOTH en and de).
 */
export function mgmtDashboardLabelKey(mgmtRole: MgmtDashboardRole): string {
  switch (mgmtRole) {
    case 'CADMN': return 'dashboard.cadmn';
    case 'DEPT': return 'dashboard.dept';
    case 'AUDT': return 'dashboard.auditor';
    default: return 'dashboard.management';
  }
}

/**
 * Translation key for the joined "<Role> Dashboard" breadcrumb crumb
 * `DashboardPageComponent.publishBreadcrumb` publishes to `DynamicBreadcrumbService` as a
 * `labelKey` (resolved by `BreadcrumbService`). One key per Dashboard view, already
 * carrying the "Dashboard" suffix in its EN value (`en.ts`'s `breadcrumbs.*`) so the
 * join happens once, in the dictionary, not by string-concatenating a translated
 * fragment with an untranslated " Dashboard" literal.
 * @param activeRole Which Dashboard view is rendered.
 * @param mgmtRole The resolved Management-view variant (ignored unless `activeRole`
 *   is `'management'`).
 * @returns A `breadcrumbs.*` key, e.g. `'breadcrumbs.departmentHeadDashboard'`.
 */
export function dashboardBreadcrumbKey(activeRole: DashboardRole, mgmtRole: MgmtDashboardRole): string {
  if (activeRole === 'performer') return 'breadcrumbs.performerDashboard';
  if (activeRole === 'reviewer') return 'breadcrumbs.reviewerDashboard';
  switch (mgmtRole) {
    case 'CADMN': return 'breadcrumbs.companyAdminDashboard';
    case 'DEPT': return 'breadcrumbs.departmentHeadDashboard';
    case 'AUDT': return 'breadcrumbs.auditorDashboard';
    default: return 'breadcrumbs.managementDashboard';
  }
}

/** Root router path of the Dashboard feature. */
export const DASHBOARD_PATH = '/dashboard';

/**
 * Full router path of one Dashboard role view (a sidebar Dashboard sub-item's
 * destination), e.g. `/dashboard/performer`.
 * @param role The role view.
 * @returns The absolute router path.
 */
export function dashboardRolePath(role: DashboardRole): string {
  return `${DASHBOARD_PATH}/${role}`;
}

/**
 * True when `url` is inside the Dashboard feature (`/dashboard` or a role route
 * under it). Used by the shell to recognise an intra-dashboard sidebar navigation
 * — the new "tab switch" — which must preserve the dashboard's persisted filter
 * state exactly as the old in-page tab band did.
 * A raw `Router.url` may carry a query string or fragment (e.g.
 * `/dashboard?foo=1`); those are stripped before matching so the URL is still
 * recognised as a dashboard URL. This mirrors the normalisation
 * `findActiveSidebarChildId` performs, so both branches of the shell's
 * active-sidebar-child derivation share one URL semantics.
 * @param url A router URL (e.g. `Router.url` or a sidebar item's route).
 * @returns Whether the URL belongs to the Dashboard feature.
 */
export function isDashboardUrl(url: string): boolean {
  const path = url.split('?')[0].split('#')[0];
  return path === DASHBOARD_PATH || path.startsWith(`${DASHBOARD_PATH}/`);
}

/** Sidebar id of the Dashboard parent item (also its route `sidebarKey` fallback). */
export const DASHBOARD_SIDEBAR_ID = 'dashboard';

/** Sidebar id of the Workspace item (see {@link shouldHideWorkspaceForAuditor}). */
const WORKSPACE_SIDEBAR_ID = 'workspace';

/**
 * Whether the Workspace sidebar item must be hidden for this user: only when the
 * resolved Management variant is Auditor AND the payload holds neither a
 * Performer nor a Reviewer row. An Auditor who ALSO holds P/R still needs
 * Workspace for that other work — the hide is scoped to a user whose only
 * dashboard capability here is Auditor.
 * @param tabs Resolved view flags (see {@link resolveDashboardRoleTabs}).
 * @returns Whether Workspace should be hidden from the sidebar for this user.
 */
export function shouldHideWorkspaceForAuditor(tabs: DashboardRoleTabs): boolean {
  return tabs.mgmtRole === 'AUDT' && !tabs.showPerformer && !tabs.showReviewer;
}

/**
 * Sidebar item id of one Dashboard role sub-item (the menu entries need unique
 * ids for click handling). NOT a route `sidebarKey`: every Dashboard route —
 * role routes included — carries the parent `sidebarKey: 'dashboard'`, exactly
 * like the Documents children carry `'documents'`, so the parent row is what
 * lights while a role view is open and no sub-item is individually highlighted
 * (per design; see dashboard.routes.spec.ts).
 * @param role The role view.
 * @returns The sidebar id, e.g. `dashboard-performer`.
 */
export function dashboardRoleSidebarId(role: DashboardRole): string {
  return `${DASHBOARD_SIDEBAR_ID}-${role}`;
}

/**
 * Builds the sidebar Dashboard sub-items (the former role TABS) for the given
 * `get-user-license-roles` payload: one child per available role view, in the tab
 * band's order (Management, Performer, Reviewer), each labelled with the same
 * i18n key its tab used — the Management child's label follows the resolved
 * variant (Company Admin / Department Head / Auditor / Management).
 * @param roles Role rows prefetched at login (`UserService.getUserLicenseRoles`).
 * @returns Sidebar children for the Dashboard item — EMPTY for an empty payload
 *   (no view is authorized, mirroring the page shell's no-roles popup; see
 *   {@link withDashboardSidebarChildren} for how an empty list renders).
 */
export function dashboardSidebarChildren(roles: DropdownOption[]): SidebarNavItem[] {
  const tabs = resolveDashboardRoleTabs(roles);
  const children: SidebarNavItem[] = [];
  if (tabs.showManagement) {
    children.push({
      id: dashboardRoleSidebarId('management'),
      label: mgmtDashboardLabelKey(tabs.mgmtRole),
      route: dashboardRolePath('management'),
    });
  }
  if (tabs.showPerformer) {
    children.push({
      id: dashboardRoleSidebarId('performer'),
      label: 'dashboard.performer',
      route: dashboardRolePath('performer'),
    });
  }
  if (tabs.showReviewer) {
    children.push({
      id: dashboardRoleSidebarId('reviewer'),
      label: 'dashboard.reviewer',
      route: dashboardRolePath('reviewer'),
    });
  }
  return children;
}

/**
 * Returns `items` with the Dashboard entry expanded into a Documents-style
 * drop-down: chevron on, children = {@link dashboardSidebarChildren} for the
 * given roles. When the roles yield NO children (pre-login / SSR / a confirmed
 * role-less payload) the Dashboard entry is left as the base list's plain LEAF:
 * `SidebarComponentComponent.onNavigate` only toggles a sub-menu for a parent
 * with a non-empty `children` list, so the leaf still navigates to `/dashboard`,
 * where the shell shows the "No License Roles Assigned" popup (or, pre-resolve,
 * the roles land and the next navigation re-derives the drop-down). Every other
 * item passes through untouched; the input array is not mutated. Called by the
 * shell (AppComponent) on every navigation, mirroring how it already refreshes
 * the sidebar role gate.
 *
 * Also drops the Workspace item entirely for a user this payload resolves to a
 * pure Auditor (see {@link shouldHideWorkspaceForAuditor}) — an Auditor who also
 * holds Performer/Reviewer keeps it.
 * @param items The base sidebar items (see `SIDEBAR_NAV_ITEMS`).
 * @param roles Role rows prefetched at login.
 * @returns A new items array with the Dashboard drop-down resolved and Workspace
 *   hidden for a pure Auditor.
 */
export function withDashboardSidebarChildren(
  items: SidebarNavItem[],
  roles: DropdownOption[],
): SidebarNavItem[] {
  const tabs = resolveDashboardRoleTabs(roles);
  const children = dashboardSidebarChildren(roles);
  const hideWorkspace = shouldHideWorkspaceForAuditor(tabs);
  return items
    .filter(item => !(hideWorkspace && item.id === WORKSPACE_SIDEBAR_ID))
    .map(item =>
      item.id === DASHBOARD_SIDEBAR_ID && children.length
        ? { ...item, hasChevron: true, children }
        : item,
    );
}
